import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import { useVideoPopup } from './VideoPopupContext';
import './VotingAnalysis.css';

// Fiscal/reporting-year boundary, driven by the tenant's fiscal_year_start_month
// (1-12). 7 = school year (Jul 1 -> next Jun 30); 1 = calendar year (cities).
// periodStartYearFor returns the starting year of the period a date falls in.
// NOTE: startMonth=7 reproduces the previous school-year behavior exactly.
const periodStartYearFor = (iso, startMonth = 7) => {
  if (!iso) return null;
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0-11
  return m >= (startMonth - 1) ? y : y - 1;
};
const periodLabel = (startYear, startMonth = 7) => {
  if (startMonth === 1) return String(startYear); // calendar year -> "2025"
  const yy = String((startYear + 1) % 100).padStart(2, '0');
  return `${startYear}–${yy}`;                     // fiscal/school year -> "2025–26"
};

const normVote = (v) => String(v || '').toLowerCase();
const normConfidence = (v) => String(v || '').toLowerCase();

const cellClass = (entry) => {
  if (!entry) return 'vote-cell vote-empty';
  const val = normVote(entry.vote);
  if (val === 'yes') return 'vote-cell vote-yes';
  if (val === 'no') return 'vote-cell vote-no';
  if (val === 'abstain') return 'vote-cell vote-abstain';
  if (val === 'absent') return 'vote-cell vote-absent';
  return 'vote-cell vote-unknown';
};

const cellLabel = (entry) => {
  if (!entry) return '—';
  const val = normVote(entry.vote);
  if (val === 'yes') return 'Yes';
  if (val === 'no') return 'No';
  if (val === 'abstain') return 'Abstain';
  if (val === 'absent') return 'Absent';
  return '—';
};

const cellTitle = (entry) => {
  if (!entry) return 'No vote recorded for this member.';
  const val = normVote(entry.vote);
  const conf = normConfidence(entry.vote_confidence);
  if (val === 'unknown') {
    return 'Not detected in transcript — likely a transcript gap, ambiguous attribution, or a roll call that was not enunciated.';
  }
  if (val === 'absent') return 'Confirmed not present for this vote.';
  if (val === 'abstain') return 'Explicit abstention or recusal.';
  const evidence = entry.evidence ? `\nEvidence: ${entry.evidence}` : '';
  if (conf && conf !== 'high') return `Voted ${val} (confidence: ${conf}).${evidence}`;
  return `Voted ${val}.${evidence}`;
};

const outcomeIsUnclear = (row) => {
  const o = String(row.outcome || '').trim().toLowerCase();
  return o === 'unknown' || o === '';
};

// A member's vote "could not be heard" when their column has an entry but the
// vote value is not one of the recognized outcomes (i.e. cellClass -> vote-unknown).
// An absent entry (cellClass -> vote-empty) means no record, not an unheard vote.
const voteUnheard = (entry) => {
  if (!entry) return false;
  return !['yes', 'no', 'abstain', 'absent'].includes(normVote(entry.vote));
};

function VotingAnalysis() {
  const { abbreviation } = useParams();
  const { showPopup } = useVideoPopup();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [meetingsByDate, setMeetingsByDate] = useState({});

  const [yearFilter, setYearFilter] = useState('current'); // 'current' | 'all' | <startYear:number>
  const [sortConfig, setSortConfig] = useState({ key: 'voteDate', direction: 'desc' });
  // Deep link: #vote-<id> in the URL (e.g. from a Community Voice follow-up
  // flag) scrolls to and highlights that vote row after load.
  const [highlightId, setHighlightId] = useState(null);
  // Per-tenant fiscal-year config (defaults reproduce school-district behavior).
  const [fyStartMonth, setFyStartMonth] = useState(7);
  const [entityType, setEntityType] = useState('school_board');
  const [memberOrder, setMemberOrder] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const { data: district, error: distError } = await supabase
        .from('Districts')
        .select('id, fiscal_year_start_month, entity_type, voting_member_order')
        .eq('abbreviation', abbreviation)
        .single();

      if (cancelled) return;
      if (distError || !district) {
        setError(distError || { message: 'District not found' });
        setLoading(false);
        return;
      }

      const fy = district.fiscal_year_start_month || 7;
      setFyStartMonth(fy);
      setEntityType(district.entity_type || 'school_board');
      setMemberOrder(district.voting_member_order || []);

      // Page past the API row cap (see fetchAllRows). Votings lists its columns
      // explicitly: `*` also pulls each row's embedding vector, which is most of
      // the payload and unused here.
      const [votingRes, meetingsRes] = await Promise.all([
        fetchAllRows(() =>
          supabase
            .from('Votings')
            .select('id, voteDate, category, issue, outcome, votes, timestamp, metadata, outcome_confidence')
            .eq('districtId', district.id)
            .order('id')
        ),
        fetchAllRows(() =>
          supabase
            .from('Meetings')
            .select('date, youtube_link, part')
            .eq('districtId', district.id)
            .order('id')
        ),
      ]);

      if (cancelled) return;

      if (votingRes.error) {
        setError(votingRes.error);
        setLoading(false);
        return;
      }

      const meetingMap = {};
      for (const m of meetingsRes.data || []) {
        if (!m.youtube_link) continue;
        if (!meetingMap[m.date]) meetingMap[m.date] = m.youtube_link;
      }

      const enriched = (votingRes.data || []).map((row) => {
        let votes = [];
        if (Array.isArray(row.votes)) votes = row.votes;
        else if (typeof row.votes === 'string') {
          try { votes = JSON.parse(row.votes); } catch { votes = []; }
        }
        const isV2 = !!row.metadata;
        const md = row.metadata || {};
        const alerts = Array.isArray(md.alerts) ? md.alerts : [];
        const memberHasUncertainty = votes.some((v) => {
          const vv = normVote(v.vote);
          const cc = normConfidence(v.vote_confidence);
          return vv === 'unknown' || cc === 'unknown' || cc === 'low';
        });
        const outcomeShaky = ['low', 'medium', 'unknown'].includes(normConfidence(row.outcome_confidence));
        const warnAlerts = alerts.filter((a) => a && (a.severity === 'warning' || a.severity === 'error'));
        const hasUncertainty = isV2 && (memberHasUncertainty || outcomeShaky || warnAlerts.length > 0 || outcomeIsUnclear(row));
        return {
          ...row,
          parsedVotes: votes,
          isV2,
          alerts,
          warnAlerts,
          hasUncertainty,
          period: periodStartYearFor(row.voteDate, fy),
        };
      });

      setMeetingsByDate(meetingMap);
      setRows(enriched);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [abbreviation]);

  const currentPeriod = useMemo(
    () => periodStartYearFor(new Date().toISOString().slice(0, 10), fyStartMonth),
    [fyStartMonth]
  );

  // Resolve a #vote-<id> deep link once rows are loaded: make sure the target
  // row's year is visible (the default filter is the current period), then
  // mark it for scroll + highlight.
  useEffect(() => {
    if (!rows.length) return;
    const m = window.location.hash.match(/^#vote-(\d+)$/);
    if (!m) return;
    const id = Number(m[1]);
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (row.period != null && row.period !== currentPeriod) {
      setYearFilter(String(row.period));
    }
    setHighlightId(id);
  }, [rows, currentPeriod]);

  // Scroll once the row exists in the DOM; let the highlight fade after a bit.
  useEffect(() => {
    if (highlightId == null) return undefined;
    const el = document.getElementById(`vote-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightId(null), 6000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const yearOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.period).filter((y) => y != null));
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (yearFilter === 'current') {
      result = result.filter((r) => r.period === currentPeriod);
    } else if (yearFilter !== 'all') {
      const y = Number(yearFilter);
      result = result.filter((r) => r.period === y);
    }
    return result;
  }, [rows, yearFilter, currentPeriod]);

  // Label vocabulary: schools say "School year"; calendar-year tenants (cities)
  // say "Year". fyStartMonth=7 keeps the exact school-district wording.
  const isCalendarFY = fyStartMonth === 1 || entityType === 'city_council';
  const periodWord = isCalendarFY ? 'Year' : 'School Year';
  const periodWordLower = isCalendarFY ? 'Year' : 'School year';

  const sortedRows = useMemo(() => {
    const { key, direction } = sortConfig;
    const mul = direction === 'asc' ? 1 : -1;
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      let cmp = 0;
      if (key === 'voteDate') {
        const dA = new Date(a.voteDate).getTime();
        const dB = new Date(b.voteDate).getTime();
        if (dA !== dB) return (dA - dB) * mul;
        // tiebreaker: chronological by timestamp regardless of direction
        return (a.timestamp || '').localeCompare(b.timestamp || '');
      }
      if (key === 'category' || key === 'issue' || key === 'outcome') {
        cmp = String(a[key] || '').localeCompare(String(b[key] || ''));
      } else {
        const va = a.parsedVotes.find((v) => v.name === key)?.vote || '';
        const vb = b.parsedVotes.find((v) => v.name === key)?.vote || '';
        cmp = String(va).localeCompare(String(vb));
      }
      return cmp * mul;
    });
    return arr;
  }, [filteredRows, sortConfig]);

  // ── Board composition over time ────────────────────────────────────────
  // A board turns over mid-period by construction: elections are in November
  // but a school fiscal year starts in July, so any single-year view can span
  // two different boards. Unioning every name into one column set then makes
  // a departed member look like they were still seated and simply not voting.
  //
  // Tenure is derived from the data (first/last meeting a member recorded a
  // vote in) over the UNFILTERED rows, so a seat is known even when the year
  // filter shows only part of it. Using a span rather than per-meeting
  // membership is also what makes this noise-tolerant: a member missing from
  // one meeting mid-tenure is still seated (their cell correctly reads "no
  // vote recorded"), and only a real departure moves the end of the span.
  const memberTenure = useMemo(() => {
    const t = {};
    rows.forEach((r) => {
      if (!r.voteDate) return;
      r.parsedVotes.forEach((v) => {
        if (!v.name) return;
        const cur = t[v.name];
        if (!cur) {
          t[v.name] = { first: r.voteDate, last: r.voteDate, dates: new Set([r.voteDate]) };
        } else {
          if (r.voteDate < cur.first) cur.first = r.voteDate;
          if (r.voteDate > cur.last) cur.last = r.voteDate;
          cur.dates.add(r.voteDate);
        }
      });
    });
    return t;
  }, [rows]);

  // Only members seen in 2+ distinct meetings may define an era boundary. A
  // name appearing in exactly one meeting across the whole archive is far
  // more likely an extraction artifact (a misheard surname) than a real
  // trustee, and without this guard each such artifact would mint its own
  // spurious one-meeting "board". Artifacts still get a column in the era
  // they appear in — they just don't split the table.
  const eraDefiningMembers = useMemo(
    () => Object.keys(memberTenure).filter((n) => memberTenure[n].dates.size >= 2),
    [memberTenure]
  );

  const seatedOn = useCallback(
    (date) =>
      eraDefiningMembers
        .filter((n) => date >= memberTenure[n].first && date <= memberTenure[n].last)
        .sort(),
    [eraDefiningMembers, memberTenure]
  );

  const orderColumns = useCallback(
    (names) => {
      const prioritized = memberOrder.filter((n) => names.has(n));
      const rest = Array.from(names).filter((n) => !memberOrder.includes(n));
      return [...prioritized, ...rest];
    },
    [memberOrder]
  );

  const latestVoteDate = useMemo(
    () => rows.reduce((max, r) => (r.voteDate > max ? r.voteDate : max), ''),
    [rows]
  );

  // One table per board era. Rows keep whatever sort the user picked; the
  // eras themselves run chronologically. When the visible range sits inside a
  // single board (the common case) this collapses to exactly one table and
  // the era heading is suppressed, so the normal view is unchanged.
  const eras = useMemo(() => {
    const byKey = new Map();
    sortedRows.forEach((r) => {
      const seated = seatedOn(r.voteDate);
      const key = seated.join('|') || '(unknown)';
      let era = byKey.get(key);
      if (!era) {
        era = { key, rows: [], names: new Set(), first: r.voteDate, last: r.voteDate };
        byKey.set(key, era);
      }
      era.rows.push(r);
      if (r.voteDate < era.first) era.first = r.voteDate;
      if (r.voteDate > era.last) era.last = r.voteDate;
      r.parsedVotes.forEach((v) => v.name && era.names.add(v.name));
    });
    const oldestFirst = sortConfig.key === 'voteDate' && sortConfig.direction === 'asc';
    const list = Array.from(byKey.values()).sort((a, b) =>
      a.first === b.first ? 0 : (a.first < b.first ? -1 : 1) * (oldestFirst ? 1 : -1)
    );
    return list.map((e) => ({ ...e, columns: orderColumns(e.names) }));
  }, [sortedRows, seatedOn, orderColumns, sortConfig]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const sortArrow = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear() - 2000}`;
  };

  const handleTimestampClick = (row, e) => {
    const youtube = meetingsByDate[row.voteDate];
    if (!youtube || !row.timestamp) return;
    showPopup(row.timestamp, youtube, e.currentTarget);
  };

  const renderTable = (tableRows, columns) => (
      <div className="table-scroll-container">
        <table className="voting-table">
          <colgroup>
            <col className="col-date" />
            <col className="col-category" />
            <col className="col-issue" />
            <col className="col-outcome" />
            {columns.map((_, i) => (
              <col key={i} className="col-member" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th onClick={() => handleSort('voteDate')}>
                Date{sortArrow('voteDate')}
              </th>
              <th onClick={() => handleSort('category')}>
                Category{sortArrow('category')}
              </th>
              <th onClick={() => handleSort('issue')}>
                Issue{sortArrow('issue')}
              </th>
              <th onClick={() => handleSort('outcome')}>
                Outcome{sortArrow('outcome')}
              </th>
              {columns.map((m) => (
                <th key={m} className="th-member" onClick={() => handleSort(m)}>
                  {m}
                  {sortArrow(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((r) => {
              const voteByName = {};
              r.parsedVotes.forEach((v) => {
                if (v.name) voteByName[v.name] = v;
              });
              const youtube = meetingsByDate[r.voteDate];
              const timestampClickable = !!(r.timestamp && youtube);
              const unclear = outcomeIsUnclear(r);
              const alertTooltip = r.warnAlerts.length
                ? r.warnAlerts.map((a) => `• ${a.message}`).join('\n')
                : '';
              return (
                <tr
                  key={r.id}
                  id={`vote-${r.id}`}
                  className={[
                    r.hasUncertainty ? 'has-uncertainty' : '',
                    r.id === highlightId ? 'vote-row-highlight' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="cell-date">
                    <div className="cell-date-day">{formatDate(r.voteDate)}</div>
                    {r.timestamp && (
                      timestampClickable ? (
                        <button
                          type="button"
                          className="cell-timestamp-btn"
                          onClick={(e) => handleTimestampClick(r, e)}
                          title="Watch this vote in the meeting recording"
                        >
                          ▶ {r.timestamp}
                        </button>
                      ) : (
                        <span
                          className="cell-timestamp-muted"
                          title="Recording not available for this meeting"
                        >
                          {r.timestamp}
                        </span>
                      )
                    )}
                  </td>
                  <td>{r.category}</td>
                  <td className="cell-issue">{r.issue}</td>
                  <td
                    className={
                      unclear
                        ? 'cell-outcome cell-outcome-unclear'
                        : 'cell-outcome'
                    }
                  >
                    {unclear ? (
                      <span title={alertTooltip || 'Outcome could not be determined from the transcript.'}>
                        Unclear
                      </span>
                    ) : (
                      <span title={alertTooltip || undefined}>{r.outcome}</span>
                    )}
                  </td>
                  {columns.map((m) => {
                    const entry = voteByName[m];
                    const conf = normConfidence(entry?.vote_confidence);
                    const lowConf = conf === 'low' || conf === 'medium';
                    const unheard = voteUnheard(entry);
                    return (
                      <td
                        key={m}
                        className={cellClass(entry)}
                        title={cellTitle(entry)}
                      >
                        {unheard ? (
                          <span
                            className="vote-unheard-mark"
                            role="img"
                            aria-label="vote could not be heard"
                          >
                            ⚠
                          </span>
                        ) : (
                          <>
                            {cellLabel(entry)}
                            {lowConf && <span className="vote-lowconf-mark"> ?</span>}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  );

  if (loading) return <div className="voting-analysis">Loading…</div>;
  if (error) return <div className="voting-analysis">Error: {error.message}</div>;

  return (
    <div className="voting-analysis">
      <div className="voting-controls">
        <label className="voting-control">
          <span className="voting-control-label">{periodWordLower}</span>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value="current">
              {periodLabel(currentPeriod, fyStartMonth)} {periodWord} (current)
            </option>
            <option value="all">All time</option>
            {yearOptions
              .filter((y) => y !== currentPeriod)
              .map((y) => (
                <option key={y} value={y}>
                  {periodLabel(y, fyStartMonth)} {periodWord}
                </option>
              ))}
          </select>
        </label>

        <div className="voting-count">
          {sortedRows.length} vote{sortedRows.length === 1 ? '' : 's'} shown
        </div>
      </div>

      <p className="voting-honesty-note">
        A <span className="vote-unheard-inline">⚠</span> in a member's column marks a vote that
        could not be heard in the recording (roll call not enunciated, audio gap, or ambiguous
        attribution) — a transcript limitation, not a missing record. Cells showing{' '}
        <span className="vote-unknown-inline">—</span> mean no vote was recorded for that member, and
        a <span className="vote-lowconf-inline">?</span> after a vote flags lower confidence in that
        transcript reading. Click a <span className="cell-timestamp-inline">▶ time</span> to watch that moment in the meeting video.
      </p>

      {sortedRows.length === 0 ? (
        <div className="voting-empty">No votes match the current filter.</div>
      ) : (
        eras.map((era) => (
          <section key={era.key} className="voting-era">
            {eras.length > 1 && (
              <header className="voting-era-header">
                <h3 className="voting-era-title">
                  Board seated {formatDate(era.first)} –{' '}
                  {era.last === latestVoteDate ? 'present' : formatDate(era.last)}
                </h3>
                <p className="voting-era-members">
                  {era.columns.join(' · ')}
                  <span className="voting-era-count">
                    {' '}· {era.rows.length} vote{era.rows.length === 1 ? '' : 's'}
                  </span>
                </p>
              </header>
            )}
            {renderTable(era.rows, era.columns)}
          </section>
        ))
      )}
    </div>
  );
}

export default VotingAnalysis;
